// src/components/overview/RunningSyncCard.jsx
import React from 'react';
import { formatDateTime, formatDuration, formatSyncType } from '../../utils/formatUtils';

/* ── Helpers ──────────────────────────────────────────────── */

const calculateProgressPercentage = (progress) => {
  if (!progress || !progress.total) return 0;
  return Math.min(100, Math.round((progress.processed / progress.total) * 100));
};

const getTypeIcon = (type) => {
  if (!type) return 'bi-question-circle';
  if (type.includes('course')) return 'bi-book';
  if (type.includes('chapter')) return 'bi-list-nested';
  if (type.includes('content')) return 'bi-file-earmark-text';
  if (type.includes('mentor')) return 'bi-person';
  if (type.includes('attempts')) return 'bi-clipboard-check';
  if (type.includes('batch')) return 'bi-people';
  if (type.includes('users') || type === 'users-sync') return 'bi-people-fill';
  return 'bi-arrow-repeat';
};

/* ── Component ────────────────────────────────────────────── */

const RunningSyncCard = ({ sync }) => {
  if (!sync) return null;

  const { id, type, progress, logs } = sync;
  const progressPercent = calculateProgressPercentage(progress);
  const isUserSync = type && (type.includes('users') || type === 'users-sync');
  const recentLogs = logs ? [...logs].reverse().slice(0, 5) : [];

  return (
    <div className="theme-card running-sync-card">
      {/* Header */}
      <div className="running-sync-header">
        <div className="running-sync-title">
          <i
            className={`bi ${getTypeIcon(type)} running-sync-type-icon`}
            aria-hidden="true"
          />
          <span className="running-sync-type-label">{formatSyncType(type)}</span>
        </div>
        <div className="running-sync-elapsed">
          <i className="bi bi-clock me-1" aria-hidden="true" />
          {formatDuration(sync)}
        </div>
      </div>

      <div className="running-sync-id">{id}</div>

      {/* Progress bar */}
      <div className="running-sync-progress-row">
        <span className="running-sync-progress-label">Progress</span>
        <span className="running-sync-progress-pct">{progressPercent}%</span>
      </div>
      <div className="running-sync-progress-track">
        <div
          className="running-sync-progress-fill"
          style={{ width: `${progressPercent}%` }}
          role="progressbar"
          aria-valuenow={progressPercent}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>

      {/* Stats row */}
      <div className="running-sync-stats">
        <div className="running-sync-stat">
          <span className="running-sync-stat-label">Processed</span>
          <span className="running-sync-stat-value" style={{ color: 'var(--text-primary)' }}>
            {progress?.processed ?? 0}/{progress?.total ?? '?'}
          </span>
        </div>

        {isUserSync && progress?.currentStudent > 0 && (
          <div className="running-sync-stat">
            <span className="running-sync-stat-label">Current Student</span>
            <span className="running-sync-stat-value" style={{ color: 'var(--accent-blue)' }}>
              {progress.currentStudent}
            </span>
          </div>
        )}

        <div className="running-sync-stat">
          <span className="running-sync-stat-label">Created</span>
          <span className="running-sync-stat-value" style={{ color: 'var(--accent-green)' }}>
            {progress?.created ?? 0}
          </span>
        </div>

        <div className="running-sync-stat">
          <span className="running-sync-stat-label">Updated</span>
          <span className="running-sync-stat-value" style={{ color: 'var(--accent-blue)' }}>
            {progress?.updated ?? 0}
          </span>
        </div>

        <div className="running-sync-stat">
          <span className="running-sync-stat-label">Failed</span>
          <span className="running-sync-stat-value" style={{ color: 'var(--accent-red)' }}>
            {progress?.failed ?? 0}
          </span>
        </div>
      </div>

      {/* Activity log */}
      <div className="running-sync-log-section">
        <div className="running-sync-log-title">
          <i className="bi bi-activity me-1" aria-hidden="true" />
          Recent Activity
        </div>
        <div className="running-sync-log">
          {recentLogs.length > 0 ? (
            recentLogs.map((entry, idx) => (
              <div key={idx} className="running-sync-log-entry">
                <span className="running-sync-log-time">
                  {entry.time ? formatDateTime(entry.time) : ''}
                </span>
                <span className="running-sync-log-msg">{entry.message}</span>
              </div>
            ))
          ) : (
            <div className="running-sync-log-empty">No activity yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RunningSyncCard;
